<script lang="ts" setup>
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { GrammarLocalLoader } from '@nora-monorepo/grammar-ui';
import type { GroupWithLocalRole, UserWithLocalRole } from '@nora-monorepo/ui-shared';
import {
  bgPrimary,
  textPrimary,
  useDeviceStore,
} from '@nora-monorepo/ui-shared';

import { ConversationModalActionState } from '#nora-chat-ui/components/modals/new-conversation/conversation-modal-types';
import ConversationModal from '#nora-chat-ui/components/modals/new-conversation/ConversationModal.vue';
import ViewConversation from '#nora-chat-ui/components/open-conversations/ViewConversation.vue';
import { useConversationStore } from '#nora-chat-ui/stores/conversation/conversation-store';

const MIN_CONVERSATION_WIDTH = 400;

const conversationStore = useConversationStore();
const deviceStore = useDeviceStore();
const { t } = useI18n();
const showCreateConversationModal = ref(false);
const initialUsers = ref<UserWithLocalRole['user'][]>([]);
const initialGroups = ref<GroupWithLocalRole['group'][]>([]);

const { isMobile } = storeToRefs(deviceStore);

const conversationContainer = ref<HTMLElement>();
const { getVisibleConversations } = storeToRefs(conversationStore);

const gridColumnClassName = computed(() => {
  if (isMobile?.value) {
    return 'grid-cols-1';
  }

  const length = Math.min(getVisibleConversations.value.length, 16);

  if (!conversationContainer.value) {
    return `grid-cols-${length}x${MIN_CONVERSATION_WIDTH}`;
  }

  const width = conversationContainer.value.clientWidth;

  if (length * MIN_CONVERSATION_WIDTH < width) {
    return `grid-cols-${length}`;
  }

  return `grid-cols-${length}x${MIN_CONVERSATION_WIDTH}`;
});

function openCreateConversationModal() {
  initialUsers.value = [];
  initialGroups.value = [];
  showCreateConversationModal.value = true;
}

function closeCreateConversationModal() {
  showCreateConversationModal.value = false;
  initialUsers.value = [];
  initialGroups.value = [];
}
</script>

<template>
  <ConversationModal
    :is-open="showCreateConversationModal"
    :mode="ConversationModalActionState.Create"
    :initial-users="initialUsers"
    :initial-groups="initialGroups"
    @close="closeCreateConversationModal"
  />
  <div
    v-if="!conversationStore.conversations.length"
    class="grow shrink flex place-content-center h-full"
    :class="bgPrimary"
  >
    <div
      class="w-full h-full flex flex-col items-center justify-center"
      :class="textPrimary"
    >
      <div>{{ t('conversation.noConversations') }}</div>
      <div>
        <a class="underline cursor-pointer" @click="openCreateConversationModal()">{{
          t('conversation.createNewConversation')
        }}</a>.
      </div>
    </div>
  </div>
  <div
    v-else-if="!getVisibleConversations.length"
    class="grow shrink flex place-content-center h-full"
    :class="bgPrimary"
  >
    <div
      class="w-full h-full flex flex-col items-center justify-center"
      :class="textPrimary"
    >
      <div>
        {{ t('conversation.noOpenConversations') }}
      </div>
      <div>
        {{ t('conversation.selectOneToOpen') }} {{ t('or') }}&nbsp;
        <a class="underline cursor-pointer" @click="openCreateConversationModal()">{{
          t('conversation.orCreateNewConversation')
        }}</a>.
      </div>
    </div>
  </div>
  <div
    v-if="getVisibleConversations.length > 0"
    ref="conversationContainer"
    class="grow shrink grid overflow-x-auto"
    :class="gridColumnClassName"
  >
    <template
      v-for="conversationState of getVisibleConversations"
      :key="conversationState.conversationListResult.id"
    >
      <ViewConversation
        v-if="!conversationState.loading"
        :conversation-state="conversationState"
      />

      <div
        v-if="conversationState.loading"
        class="flex items-center justify-center h-full"
      >
        <GrammarLocalLoader />
      </div>
    </template>
  </div>
</template>
